import { useState } from 'react';
import FooterSection from '@/pages/home/components/FooterSection';

export default function ContactPage() {
  const [formData, setFormData] = useState({ email: '', name: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }
    setError('');
    setSubmitting(true);
    const body = new URLSearchParams({
      email: formData.email,
      name: formData.name,
      subject: formData.subject,
      message: formData.message,
    });
    await fetch('https://readdy.ai/api/form/d7e1iockpdh0a4ae08qg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <main className="w-full">
      {/* Page Hero */}
      <section className="relative w-full h-48 md:h-64 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/aff71df525aaa4a90840142f0b740aa7.jpg"
          alt="Contact Us"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#2a0a3d]/90"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pt-16">
          <p className="text-[#ffd700] text-xs font-bold tracking-widest uppercase mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Get In Touch
          </p>
          <h1 className="text-4xl md:text-5xl font-black text-white uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Contact Us
          </h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="w-full px-6 md:px-12 py-14 bg-[#f8f6fb]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">

            {/* Form */}
            <div className="bg-white rounded-lg border border-gray-100 p-8">
              <h2 className="text-xl font-black text-[#2a0a3d] uppercase mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Send an Email to the Site Administrator
              </h2>
              <p className="text-gray-400 text-xs mb-6 tracking-wide">All fields marked with * are required.</p>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-[#4a1a6b]/10">
                    <i className="ri-check-line text-2xl text-[#4a1a6b]"></i>
                  </div>
                  <h3 className="text-lg font-black text-[#2a0a3d] mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>Message Sent!</h3>
                  <p className="text-gray-500 text-sm">Thanks for reaching out. We'll get back to you soon. Skol Vikings!</p>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-[#4a1a6b] transition-colors"
                      style={{ fontFamily: "'Oswald', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-[#4a1a6b] transition-colors"
                      style={{ fontFamily: "'Oswald', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="What's this about?"
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-[#4a1a6b] transition-colors"
                      style={{ fontFamily: "'Oswald', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Your message..."
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-[#4a1a6b] transition-colors resize-none"
                      style={{ fontFamily: "'Oswald', sans-serif" }}
                    />
                    <p className={`text-[10px] text-right mt-1 ${formData.message.length > 480 ? 'text-red-400' : 'text-gray-400'}`}>
                      {formData.message.length}/500
                    </p>
                  </div>
                  {error && <p className="text-red-500 text-xs">{error}</p>}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="whitespace-nowrap w-full py-3 rounded-md bg-[#4a1a6b] text-white font-black text-sm tracking-widest uppercase cursor-pointer hover:bg-[#ffd700] hover:text-[#4a1a6b] transition-all duration-300 disabled:opacity-60"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>

            {/* Logo / Info Panel */}
            <div className="flex flex-col gap-6">
              <div className="rounded-lg overflow-hidden border border-gray-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Minnesota%20Vikings%20fan%20community%20purple%20gold%20jersey%20crowd%20celebration%20US%20Bank%20Stadium%20passionate%20fans%20purple%20pride%20banner&width=600&height=400&seq=contactimg1&orientation=landscape"
                  alt="Purple Pride Community"
                  className="w-full h-56 object-cover object-top"
                />
              </div>

              <div className="bg-gradient-to-br from-[#4a1a6b] to-[#2a0a3d] rounded-lg p-6">
                <h3 className="text-lg font-black text-white uppercase mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  About Purple Pride
                </h3>
                <ul className="flex flex-col gap-3">
                  <li className="flex items-center gap-3 text-sm text-white/70">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#ffd700]/20 flex-shrink-0">
                      <i className="ri-mail-line text-[#ffd700] text-sm"></i>
                    </div>
                    info@purplepride.org
                  </li>
                  <li className="flex items-center gap-3 text-sm text-white/70">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#ffd700]/20 flex-shrink-0">
                      <i className="ri-map-pin-line text-[#ffd700] text-sm"></i>
                    </div>
                    Minneapolis, Minnesota
                  </li>
                  <li className="flex items-center gap-3 text-sm text-white/70">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#ffd700]/20 flex-shrink-0">
                      <i className="ri-global-line text-[#ffd700] text-sm"></i>
                    </div>
                    purplepride.org
                  </li>
                  <li className="flex items-center gap-3 text-sm text-white/70">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#ffd700]/20 flex-shrink-0">
                      <i className="ri-calendar-line text-[#ffd700] text-sm"></i>
                    </div>
                    Est. 1998 · Home of Vikings Fans Worldwide
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterSection />
    </main>
  );
}
